import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { useImportMutations, useImportRows } from '../api';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import { num } from '../../lib/format';
import type {
  DuplicatePolicy,
  ImportCommitResult,
  ImportPreview,
  ImportRow,
  ImportRowStatus,
  ImportUploadResult,
} from '../../lib/types';

const STEPS = [
  'Upload',
  'Sheets',
  'Preview',
  'Columns',
  'Validate',
  'Summary',
  'Confirm',
  'Done',
] as const;

const APP_FIELDS: { key: string; label: string; req?: boolean }[] = [
  { key: 'serialNumber', label: 'Serial no.' },
  { key: 'barcodeNo', label: 'Barcode no.', req: true },
  { key: 'photoId', label: 'Photo ID' },
  { key: 'size', label: 'Size' },
  { key: 'grossWeight', label: 'Gross weight', req: true },
  { key: 'stoneWeight', label: 'Stone weight' },
  { key: 'stoneAmount', label: 'Stone amount' },
];

const ROW_TONE: Record<ImportRowStatus, string> = {
  valid: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  invalid: 'bg-red-100 text-red-700',
  duplicate_in_file: 'bg-violet-100 text-violet-700',
  duplicate_in_db: 'bg-blue-100 text-blue-700',
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-blue-100 text-blue-700',
  skipped: 'bg-ink-100 text-ink-600',
  failed: 'bg-red-100 text-red-700',
};

function RowStatus({ status }: { status: ImportRowStatus }) {
  return (
    <span className={clsx('badge', ROW_TONE[status] ?? 'bg-ink-100 text-ink-600')}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function fmtSize(bytes: number) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type MapsBySheet = Record<string, Record<string, string>>;

export function ImportPage() {
  const toast = useToast();
  const { upload, commit } = useImportMutations();

  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportUploadResult | null>(null);
  const [sheetSel, setSheetSel] = useState<Record<string, boolean>>({});
  const [maps, setMaps] = useState<MapsBySheet>({});
  const [dirty, setDirty] = useState(false);
  const [policy, setPolicy] = useState<DuplicatePolicy>('skip');
  const [allowDup, setAllowDup] = useState(false);
  const [commitResult, setCommitResult] = useState<ImportCommitResult | null>(null);

  const preview = result?.preview ?? null;
  const batchId = result?.batch._id;

  const selectedSheetNames = useMemo(
    () => Object.entries(sheetSel).filter(([, v]) => v).map(([k]) => k),
    [sheetSel],
  );

  const seedFromResult = (r: ImportUploadResult) => {
    const sel: Record<string, boolean> = {};
    const m: MapsBySheet = {};
    for (const s of r.preview.sheets) {
      sel[s.sheet] = s.summary.selected;
      m[s.sheet] = { ...s.summary.columnMap };
    }
    setResult(r);
    setSheetSel(sel);
    setMaps(m);
    setDirty(false);
  };

  const runUpload = async (opts?: { selectSheets?: string[]; columnMaps?: MapsBySheet }) => {
    if (!file) return;
    try {
      const r = await upload.mutateAsync({ file, options: opts });
      seedFromResult(r);
      return r;
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Upload failed', 'error');
      throw e;
    }
  };

  const reset = () => {
    setStep(0);
    setFile(null);
    setResult(null);
    setSheetSel({});
    setMaps({});
    setDirty(false);
    setPolicy('skip');
    setAllowDup(false);
    setCommitResult(null);
  };

  // ── forecast for the summary step ──
  const forecast = useMemo(() => {
    if (!preview) return null;
    const acc = { total: 0, create: 0, update: 0, skip: 0, fail: 0 };
    for (const s of preview.sheets) {
      if (!sheetSel[s.sheet]) continue;
      const su = s.summary;
      acc.total += su.totalRows;
      acc.create += su.validRows; // fresh, non-duplicate rows
      acc.fail += su.invalidRows;
      const dbDup = su.duplicateInDb;
      const fileDup = su.duplicateInFile;
      if (policy === 'update') {
        acc.update += dbDup;
        acc.skip += fileDup;
      } else if (policy === 'create_new' && allowDup) {
        acc.create += dbDup + fileDup;
      } else {
        acc.skip += dbDup + fileDup;
      }
    }
    return acc;
  }, [preview, sheetSel, policy, allowDup]);

  const requiredMapped = (sheet: string) =>
    APP_FIELDS.filter((f) => f.req).every((f) => maps[sheet]?.[f.key]);
  const allRequiredMapped = selectedSheetNames.every(requiredMapped);
  const anyRowsSelected = preview
    ? preview.sheets.some((s) => sheetSel[s.sheet] && s.summary.totalRows > 0)
    : false;

  const goCommit = async () => {
    if (!batchId) return;
    try {
      const res = await commit.mutateAsync({
        id: batchId,
        body: {
          sheets: selectedSheetNames,
          duplicatePolicy: policy,
          allowDuplicateBarcode: policy === 'create_new' && allowDup,
        },
      });
      setCommitResult(res);
      setStep(7);
      toast(
        `Import ${res.batch.code}: ${res.created} created, ${res.updated} updated`,
        res.failed ? 'info' : 'success',
      );
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Import failed', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Import Jewellery Data</h1>
          <p className="text-sm text-ink-400">
            Load the product master from an Excel workbook — {STEPS.length} guided steps.
          </p>
        </div>
        <Link className="btn-outline" to="/admin/jewellery">
          Back to Jewellery
        </Link>
      </div>

      <Stepper step={step} />

      <div className="card p-5">
        {step === 0 && (
          <UploadStep
            file={file}
            busy={upload.isPending}
            onPick={setFile}
            onNext={async () => {
              await runUpload();
              setStep(1);
            }}
          />
        )}

        {step === 1 && preview && (
          <SheetsStep
            preview={preview}
            sel={sheetSel}
            onToggle={(name) => {
              setSheetSel((p) => ({ ...p, [name]: !p[name] }));
              setDirty(true);
            }}
          />
        )}

        {step === 2 && preview && <PreviewStep preview={preview} sel={sheetSel} maps={maps} />}

        {step === 3 && preview && (
          <ColumnsStep
            preview={preview}
            sel={sheetSel}
            maps={maps}
            onChange={(sheet, field, header) => {
              setMaps((p) => ({ ...p, [sheet]: { ...p[sheet], [field]: header } }));
              setDirty(true);
            }}
          />
        )}

        {step === 4 && batchId && (
          <ValidateStep
            key={batchId}
            preview={preview!}
            sel={sheetSel}
            batchId={batchId}
            dirty={dirty}
            canRevalidate={allRequiredMapped && anyRowsSelected}
            revalidating={upload.isPending}
            onRevalidate={async () => {
              try {
                await runUpload({
                  selectSheets: selectedSheetNames,
                  columnMaps: Object.fromEntries(
                    selectedSheetNames.map((n) => [n, maps[n] ?? {}]),
                  ),
                });
              } catch {
                /* toast already shown */
              }
            }}
          />
        )}

        {step === 5 && forecast && (
          <SummaryStep preview={preview!} sel={sheetSel} forecast={forecast} />
        )}

        {step === 6 && (
          <ConfirmStep
            policy={policy}
            allowDup={allowDup}
            forecast={forecast!}
            fileName={file?.name ?? ''}
            onPolicy={setPolicy}
            onAllowDup={setAllowDup}
          />
        )}

        {step === 7 && commitResult && (
          <DoneStep
            code={commitResult.batch.code}
            created={commitResult.created}
            updated={commitResult.updated}
            skipped={commitResult.skipped}
            failed={commitResult.failed}
            onReset={reset}
          />
        )}
      </div>

      {/* nav bar */}
      {step < 7 && (
        <div className="flex items-center justify-between">
          <button
            className="btn-ghost"
            disabled={step === 0 || upload.isPending || commit.isPending}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
          >
            ← Back
          </button>

          <div className="flex items-center gap-2">
            {step === 0 && (
              <button
                className="btn-primary"
                disabled={!file || upload.isPending}
                onClick={async () => {
                  try {
                    await runUpload();
                    setStep(1);
                  } catch {
                    /* toast already shown */
                  }
                }}
              >
                {upload.isPending ? 'Analysing…' : 'Upload & analyse'}
              </button>
            )}

            {step === 1 && (
              <button className="btn-primary" disabled={!anyRowsSelected} onClick={() => setStep(2)}>
                Continue
              </button>
            )}

            {step === 2 && (
              <button className="btn-primary" onClick={() => setStep(3)}>
                Continue
              </button>
            )}

            {step === 3 && (
              <button
                className="btn-primary"
                disabled={!allRequiredMapped}
                onClick={async () => {
                  if (dirty) {
                    try {
                      await runUpload({
                        selectSheets: selectedSheetNames,
                        columnMaps: Object.fromEntries(
                          selectedSheetNames.map((n) => [n, maps[n] ?? {}]),
                        ),
                      });
                    } catch {
                      return;
                    }
                  }
                  setStep(4);
                }}
              >
                {dirty ? 'Apply & validate' : 'Continue'}
              </button>
            )}

            {step === 4 && (
              <button
                className="btn-primary"
                disabled={dirty || upload.isPending}
                onClick={() => setStep(5)}
              >
                Continue
              </button>
            )}

            {step === 5 && (
              <button className="btn-primary" onClick={() => setStep(6)}>
                Continue
              </button>
            )}

            {step === 6 && (
              <button
                className="btn-primary"
                disabled={commit.isPending || !anyRowsSelected}
                onClick={goCommit}
              >
                {commit.isPending
                  ? 'Importing…'
                  : `Import ${num(forecast?.create ?? 0)} + update ${num(forecast?.update ?? 0)}`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── steps ───────────────────────────

function Stepper({ step }: { step: number }) {
  return (
    <ol className="flex flex-wrap gap-1 text-xs">
      {STEPS.map((label, i) => (
        <li
          key={label}
          className={clsx(
            'flex items-center gap-1.5 rounded-full px-3 py-1',
            i === step && 'bg-ink-900 text-white',
            i < step && 'bg-emerald-100 text-emerald-700',
            i > step && 'bg-ink-100 text-ink-400',
          )}
        >
          <span className="font-mono">{i + 1}</span>
          {label}
        </li>
      ))}
    </ol>
  );
}

function UploadStep({
  file,
  busy,
  onPick,
  onNext,
}: {
  file: File | null;
  busy: boolean;
  onPick: (f: File | null) => void;
  onNext: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  const take = (f?: File | null) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm|xls)$/i.test(f.name)) {
      onPick(null);
      return;
    }
    onPick(f);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-ink-900">1 · Choose the workbook</h2>
      <div
        className={clsx(
          'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-14 text-center transition',
          drag ? 'border-gold-400 bg-gold-50' : 'border-ink-200 hover:border-ink-300',
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          take(e.dataTransfer.files?.[0]);
        }}
      >
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-ink-400">
          <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <p className="text-sm font-medium text-ink-700">
          {file ? file.name : 'Drop an .xlsx / .xls file here, or click to browse'}
        </p>
        <p className="text-xs text-ink-400">{file ? fmtSize(file.size) : 'Sheets named Master / Master1 are auto-detected'}</p>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xlsm,.xls"
          className="hidden"
          onChange={(e) => take(e.target.files?.[0])}
        />
      </div>
      {file && (
        <div className="flex items-center gap-3 text-sm">
          <button className="btn-ghost px-2 py-1 text-red-600" onClick={() => onPick(null)}>
            Remove
          </button>
          <button className="btn-primary" disabled={busy} onClick={onNext}>
            {busy ? 'Analysing…' : 'Upload & analyse'}
          </button>
        </div>
      )}
    </div>
  );
}

function SheetsStep({
  preview,
  sel,
  onToggle,
}: {
  preview: ImportPreview;
  sel: Record<string, boolean>;
  onToggle: (name: string) => void;
}) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-ink-900">2 · Sheets found</h2>
      <p className="text-sm text-ink-400">
        Pick which worksheets to import. Empty sheets and non-product tabs can be left off.
      </p>
      <div className="divide-y divide-ink-50 rounded-lg border border-ink-100">
        {preview.sheets.map((s) => {
          const disabled = s.summary.totalRows === 0;
          return (
            <label
              key={s.sheet}
              className={clsx(
                'flex items-center gap-3 px-4 py-3',
                disabled ? 'opacity-50' : 'cursor-pointer hover:bg-ink-50/60',
              )}
            >
              <input
                type="checkbox"
                checked={!!sel[s.sheet] && !disabled}
                disabled={disabled}
                onChange={() => onToggle(s.sheet)}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink-800">{s.sheet}</span>
                  <span
                    className={clsx(
                      'badge',
                      s.summary.detectedProfile === 'custom'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700',
                    )}
                  >
                    {s.summary.detectedProfile === 'custom'
                      ? 'custom layout'
                      : `${s.summary.detectedProfile} profile`}
                  </span>
                </div>
                <p className="text-xs text-ink-400">
                  {num(s.summary.totalRows)} data row{s.summary.totalRows === 1 ? '' : 's'} ·{' '}
                  {s.summary.headers.length} columns
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function mappedHeaders(summary: ImportPreview['sheets'][number]['summary'], maps: MapsBySheet) {
  const map = maps[summary.sheetName] ?? summary.columnMap;
  const wanted = new Set(Object.values(map).filter(Boolean));
  return summary.headers.filter((h) => wanted.has(h));
}

function PreviewStep({
  preview,
  sel,
  maps,
}: {
  preview: ImportPreview;
  sel: Record<string, boolean>;
  maps: MapsBySheet;
}) {
  const sheets = preview.sheets.filter((s) => sel[s.sheet] && s.sampleRows.length);
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-ink-900">3 · Preview rows</h2>
      {sheets.length === 0 && <p className="text-sm text-ink-400">No rows to preview.</p>}
      {sheets.map((s) => {
        const cols = mappedHeaders(s.summary, maps);
        return (
          <div key={s.sheet} className="space-y-2">
            <p className="text-sm font-medium text-ink-700">
              {s.sheet}{' '}
              <span className="text-ink-400">
                — first {s.sampleRows.length} of {num(s.summary.totalRows)}
              </span>
            </p>
            <div className="overflow-x-auto rounded-lg border border-ink-100">
              <table className="w-full text-xs">
                <thead>
                  <tr className="table-head">
                    <th className="px-2 py-1.5 text-left">Row</th>
                    {cols.map((c) => (
                      <th key={c} className="px-2 py-1.5 text-left">
                        {c}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-50">
                  {s.sampleRows.map((r) => (
                    <tr key={r._id} className="hover:bg-ink-50/60">
                      <td className="px-2 py-1 font-mono text-ink-400">{r.sourceRow}</td>
                      {cols.map((c) => (
                        <td key={c} className="px-2 py-1">
                          {r.raw[c] == null || r.raw[c] === '' ? (
                            <span className="text-ink-300">—</span>
                          ) : (
                            String(r.raw[c])
                          )}
                        </td>
                      ))}
                      <td className="px-2 py-1">
                        <RowStatus status={r.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ColumnsStep({
  preview,
  sel,
  maps,
  onChange,
}: {
  preview: ImportPreview;
  sel: Record<string, boolean>;
  maps: MapsBySheet;
  onChange: (sheet: string, field: string, header: string) => void;
}) {
  const sheets = preview.sheets.filter((s) => sel[s.sheet]);
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-ink-900">4 · Map columns</h2>
      <p className="text-sm text-ink-400">
        Match each spreadsheet column to a product field. <b>Barcode no.</b> and{' '}
        <b>Gross weight</b> are required.
      </p>
      {sheets.map((s) => {
        const map = maps[s.sheet] ?? s.summary.columnMap;
        return (
          <div key={s.sheet} className="rounded-lg border border-ink-100 p-4">
            <p className="mb-3 text-sm font-medium text-ink-700">{s.sheet}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {APP_FIELDS.map((f) => {
                const missing = f.req && !map[f.key];
                return (
                  <label key={f.key} className="block">
                    <span className="label">
                      {f.label}
                      {f.req && <span className="text-red-500"> *</span>}
                    </span>
                    <select
                      className={clsx('input', missing && 'border-red-300 bg-red-50')}
                      value={map[f.key] ?? ''}
                      onChange={(e) => onChange(s.sheet, f.key, e.target.value)}
                    >
                      <option value="">— not mapped —</option>
                      {s.summary.headers.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ValidateStep({
  preview,
  sel,
  batchId,
  dirty,
  canRevalidate,
  revalidating,
  onRevalidate,
}: {
  preview: ImportPreview;
  sel: Record<string, boolean>;
  batchId: string;
  dirty: boolean;
  canRevalidate: boolean;
  revalidating: boolean;
  onRevalidate: () => void;
}) {
  const [tab, setTab] = useState<'all' | ImportRowStatus>('invalid');
  const rows = useImportRows(batchId, {
    status: tab === 'all' ? undefined : tab,
    limit: 100,
  });
  const problems = rows.data?.data ?? [];
  const sheets = preview.sheets.filter((s) => sel[s.sheet]);

  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-ink-900">5 · Validation results</h2>

      {dirty && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span>Sheet selection or column mapping changed since the last check.</span>
          <button
            className="btn-outline px-2 py-1 text-xs"
            disabled={!canRevalidate || revalidating}
            onClick={onRevalidate}
          >
            {revalidating ? 'Re-validating…' : 'Re-validate'}
          </button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {sheets.map((s) => {
          const su = s.summary;
          return (
            <div key={s.sheet} className="rounded-lg border border-ink-100 p-4 text-sm">
              <p className="mb-2 font-medium text-ink-700">{s.sheet}</p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <Cell label="Total rows" value={su.totalRows} />
                <Cell label="Ready to import" value={su.validRows} tone="emerald" />
                <Cell label="Warnings" value={su.warningRows} tone={su.warningRows ? 'amber' : undefined} />
                <Cell label="Invalid" value={su.invalidRows} tone={su.invalidRows ? 'red' : undefined} />
                <Cell
                  label="Duplicate in file"
                  value={su.duplicateInFile}
                  tone={su.duplicateInFile ? 'violet' : undefined}
                />
                <Cell
                  label="Already in master"
                  value={su.duplicateInDb}
                  tone={su.duplicateInDb ? 'blue' : undefined}
                />
              </dl>
            </div>
          );
        })}
      </div>

      <div>
        <div className="mb-2 flex flex-wrap gap-1 text-xs">
          {(['invalid', 'duplicate_in_db', 'duplicate_in_file', 'warning', 'all'] as const).map((t) => (
            <button
              key={t}
              className={clsx(
                'rounded-full px-3 py-1',
                tab === t ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-500 hover:bg-ink-200',
              )}
              onClick={() => setTab(t)}
            >
              {t === 'all' ? 'All rows' : t.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        {rows.isLoading ? (
          <p className="py-6 text-center text-sm text-ink-400">Loading rows…</p>
        ) : problems.length === 0 ? (
          <p className="rounded-lg border border-dashed border-ink-200 py-6 text-center text-sm text-ink-400">
            Nothing in this category.
          </p>
        ) : (
          <div className="max-h-80 overflow-auto rounded-lg border border-ink-100">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white">
                <tr className="table-head">
                  <th className="px-2 py-1.5 text-left">Sheet</th>
                  <th className="px-2 py-1.5 text-left">Row</th>
                  <th className="px-2 py-1.5 text-left">Barcode</th>
                  <th className="px-2 py-1.5 text-left">Status</th>
                  <th className="px-2 py-1.5 text-left">Messages</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-50">
                {problems.map((r: ImportRow) => (
                  <tr key={r._id}>
                    <td className="px-2 py-1 text-ink-400">{r.sheetName}</td>
                    <td className="px-2 py-1 font-mono text-ink-400">{r.sourceRow}</td>
                    <td className="px-2 py-1 font-mono">{r.parsed?.barcodeNo ?? '—'}</td>
                    <td className="px-2 py-1">
                      <RowStatus status={r.status} />
                    </td>
                    <td className="px-2 py-1 text-ink-500">{r.messages?.join('; ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStep({
  preview,
  sel,
  forecast,
}: {
  preview: ImportPreview;
  sel: Record<string, boolean>;
  forecast: { total: number; create: number; update: number; skip: number; fail: number };
}) {
  const sheets = preview.sheets.filter((s) => sel[s.sheet]);
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-ink-900">6 · Summary</h2>
      <p className="text-sm text-ink-400">
        Sheets: <b>{sheets.map((s) => s.sheet).join(', ') || 'none'}</b>. Figures below reflect the
        currently chosen duplicate policy on the next step.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Big label="Rows read" value={forecast.total} />
        <Big label="New items" value={forecast.create} tone="emerald" />
        <Big label="Updated" value={forecast.update} tone="blue" />
        <Big label="Skipped / invalid" value={forecast.skip + forecast.fail} tone="ink" />
      </div>
      <p className="text-xs text-ink-400">
        Nothing is written to the product master until you press <b>Import</b> on the final step.
      </p>
    </div>
  );
}

function ConfirmStep({
  policy,
  allowDup,
  forecast,
  fileName,
  onPolicy,
  onAllowDup,
}: {
  policy: DuplicatePolicy;
  allowDup: boolean;
  forecast: { total: number; create: number; update: number; skip: number; fail: number };
  fileName: string;
  onPolicy: (p: DuplicatePolicy) => void;
  onAllowDup: (v: boolean) => void;
}) {
  const OPTIONS: { value: DuplicatePolicy; title: string; body: string }[] = [
    { value: 'skip', title: 'Skip', body: 'Leave existing products untouched. Only brand-new barcodes are added.' },
    { value: 'update', title: 'Update', body: 'Refresh weights / size / photo ID on products that already exist.' },
    {
      value: 'create_new',
      title: 'Create new',
      body: 'Add duplicates as separate records with a suffixed barcode (needs the toggle below).',
    },
  ];
  return (
    <div className="space-y-5">
      <h2 className="text-lg font-semibold text-ink-900">7 · Confirm &amp; import</h2>
      <p className="text-sm text-ink-400">
        From <b>{fileName}</b>: about <b>{num(forecast.create)}</b> new and{' '}
        <b>{num(policy === 'update' ? forecast.update : 0)}</b> updated.
      </p>

      <div>
        <span className="label">When a barcode already exists</span>
        <div className="mt-1 grid gap-2 sm:grid-cols-3">
          {OPTIONS.map((o) => (
            <label
              key={o.value}
              className={clsx(
                'cursor-pointer rounded-lg border p-3 text-sm',
                policy === o.value ? 'border-ink-900 bg-ink-50' : 'border-ink-200 hover:border-ink-300',
              )}
            >
              <div className="flex items-center gap-2">
                <input
                  type="radio"
                  name="dup-policy"
                  checked={policy === o.value}
                  onChange={() => onPolicy(o.value)}
                />
                <span className="font-medium text-ink-800">{o.title}</span>
              </div>
              <p className="mt-1 text-xs text-ink-400">{o.body}</p>
            </label>
          ))}
        </div>
      </div>

      <label
        className={clsx(
          'flex items-center gap-2 text-sm',
          policy !== 'create_new' && 'opacity-40',
        )}
      >
        <input
          type="checkbox"
          checked={allowDup}
          disabled={policy !== 'create_new'}
          onChange={(e) => onAllowDup(e.target.checked)}
        />
        Allow duplicate barcodes (append a <code className="mx-1">-Dxxxx</code> suffix)
      </label>
    </div>
  );
}

function DoneStep({
  code,
  created,
  updated,
  skipped,
  failed,
  onReset,
}: {
  code: string;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  onReset: () => void;
}) {
  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div>
        <h2 className="text-lg font-semibold text-ink-900">8 · Import complete</h2>
        <p className="text-sm text-ink-400">Batch {code}</p>
      </div>
      <div className="mx-auto grid max-w-lg grid-cols-2 gap-3 sm:grid-cols-4">
        <Big label="Created" value={created} tone="emerald" />
        <Big label="Updated" value={updated} tone="blue" />
        <Big label="Skipped" value={skipped} tone="ink" />
        <Big label="Failed" value={failed} tone={failed ? 'red' : 'ink'} />
      </div>
      <div className="flex justify-center gap-2">
        <Link className="btn-primary" to="/admin/jewellery">
          View Jewellery
        </Link>
        <button className="btn-outline" onClick={onReset}>
          Import another file
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────── small bits ───────────────────────────

const TONE: Record<string, string> = {
  emerald: 'text-emerald-600',
  blue: 'text-blue-600',
  red: 'text-red-600',
  amber: 'text-amber-600',
  violet: 'text-violet-600',
  ink: 'text-ink-700',
};

function Cell({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <>
      <dt className="text-ink-400">{label}</dt>
      <dd className={clsx('text-right font-medium', tone ? TONE[tone] : 'text-ink-700')}>
        {num(value)}
      </dd>
    </>
  );
}

function Big({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border border-ink-100 p-3 text-center">
      <p className={clsx('text-2xl font-semibold', tone ? TONE[tone] : 'text-ink-900')}>{num(value)}</p>
      <p className="text-xs text-ink-400">{label}</p>
    </div>
  );
}
